/**
 * The cards over the audit table, in the AI usage strip's layout: a wide
 * card, a rule, then a pair. All three count what the table's filters match.
 *
 * - Events: the total with one 100% bar of its outcomes; the legend entries
 *   narrow the table to one outcome (again to clear it).
 * - Activity: entries per hour (up to two days), day (up to ~three months)
 *   or week, each column stacked by outcome, in local time or UTC.
 * - Who: the distinct actors and source IPs, one bar of the event families,
 *   and the failed sign-ins.
 *
 * The server caps the window at a year and says when it cut the range.
 * Plain HTML and CSS: no chart library.
 */

import type { AuditOutcome, AuditSummary as Summary } from '@/api/types';
import { formatInt, formatPct, share } from '@/ui/format';

const OUTCOMES: { key: AuditOutcome; label: string; className: string }[] = [
  { key: 'SUCCESS', label: 'Success', className: 'seg-ok' },
  { key: 'DENIED', label: 'Denied', className: 'seg-refused' },
  { key: 'FAILED', label: 'Failed', className: 'seg-failed' },
];

const FAMILIES: { key: string; label: string }[] = [
  { key: 'AUTHENTICATION', label: 'Authentication' },
  { key: 'USER_ADMIN', label: 'User admin' },
  { key: 'CONFIGURATION', label: 'Configuration' },
  { key: 'AI_CONFIGURATION', label: 'AI configuration' },
  { key: 'AI_USAGE', label: 'AI usage' },
  { key: 'DISCOVERY', label: 'Discovery' },
  { key: 'FEEDBACK', label: 'Feedback' },
  { key: 'API', label: 'Other API' },
  { key: 'READ', label: 'Traced reads' },
];

export function AuditSummary({
  data,
  utc,
  outcomes,
  onOutcome,
}: {
  data: Summary;
  utc: boolean;
  /** The table's outcome filter, so the legend can show which one it holds. */
  outcomes: string[];
  onOutcome: (outcomes: string[]) => void;
}) {
  return (
    <div className="metric-area">
      <div className="metric-row usage-summary">
        <EventsCard data={data} utc={utc} outcomes={outcomes} onOutcome={onOutcome} />
        <span className="metric-divider" aria-hidden="true" />
        <section className="usage-card-pair" aria-label="Activity and actors">
          <ActivityCard data={data} utc={utc} />
          <WhoCard data={data} />
        </section>
      </div>
    </div>
  );
}

function EventsCard({
  data,
  utc,
  outcomes,
  onOutcome,
}: {
  data: Summary;
  utc: boolean;
  outcomes: string[];
  onOutcome: (outcomes: string[]) => void;
}) {
  const refused = (data.outcomes.DENIED ?? 0) + (data.outcomes.FAILED ?? 0);
  const refusedPct = data.total > 0 ? (refused / data.total) * 100 : 0;
  const state = refused === 0 ? 'ok' : refusedPct < 10 ? 'warn' : 'over';
  const day = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: utc ? 'UTC' : undefined });

  return (
    <section className="metric-tile usage-card usage-card-budget" aria-label="Events">
      <div className="usage-card-head">
        <div className="usage-card-figure">
          <span className="metric-value">{formatInt(data.total)}</span>
          <span className="usage-card-of">{data.total === 1 ? 'event' : 'events'}</span>
        </div>
        {data.total > 0 && (
          <span className={`badge ${{ ok: 'badge-success', warn: 'badge-warning', over: 'badge-danger' }[state]}`}>
            {refused === 0 ? 'Nothing denied or failed' : `${formatPct(refusedPct)} denied or failed`}
          </span>
        )}
      </div>

      <div
        className="stack-bar outcome-bar"
        role="img"
        aria-label={OUTCOMES.map((o) => `${formatInt((data.outcomes[o.key] ?? 0))} ${o.label.toLowerCase()}`).join(', ')}
      >
        {OUTCOMES.map((o) => (
          <span key={o.key} className={`seg ${o.className}`} style={{ width: share((data.outcomes[o.key] ?? 0), data.total) }} />
        ))}
      </div>

      <ul className="usage-legend">
        {OUTCOMES.map((o) => {
          const only = outcomes.length === 1 && outcomes[0] === o.key;
          return (
            <li key={o.key}>
              <button
                type="button"
                className="legend-filter"
                aria-pressed={only}
                title={only ? 'Show every outcome' : `Show only ${o.label.toLowerCase()} entries`}
                onClick={() => onOutcome(only ? [] : [o.key])}
              >
                <span className={`swatch ${o.className}`} />
                {o.label} <b>{formatInt((data.outcomes[o.key] ?? 0))}</b>
              </button>
            </li>
          );
        })}
      </ul>

      <p className="usage-card-note">
        {day(data.window_from)} – {day(data.window_to)}
        {data.clamped && ' · the summary covers the last year of the range'}
      </p>
    </section>
  );
}

type Unit = 'hour' | 'day' | 'week';

const HOUR = 3600 * 1000;

/** The start of the hour holding `t`, or of its day (a week's columns start on the window's first day). */
function startOf(t: number, unit: Unit, utc: boolean): number {
  if (unit === 'hour') return Math.floor(t / HOUR) * HOUR;
  const d = new Date(t);
  return utc ? Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function next(t: number, unit: Unit, utc: boolean): number {
  if (unit === 'hour') return t + HOUR;
  const days = unit === 'day' ? 1 : 7;
  const d = new Date(t);
  return utc
    ? Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days)
    : new Date(d.getFullYear(), d.getMonth(), d.getDate() + days).getTime();
}

interface Bucket {
  start: number;
  counts: Record<AuditOutcome, number>;
  total: number;
}

function bucketUnit(span: number): Unit {
  if (span <= 48 * HOUR) return 'hour';
  return span <= 92 * 24 * HOUR ? 'day' : 'week';
}

function addOutcomes(column: Bucket, outcomes: Summary['hours'][number]['outcomes']) {
  for (const o of OUTCOMES) {
    column.counts[o.key] += outcomes[o.key] ?? 0;
    column.total += outcomes[o.key] ?? 0;
  }
}

/** The window as columns, each with the hours that fall in it added up. */
function buckets(data: Summary, utc: boolean): { unit: Unit; columns: Bucket[] } {
  const from = new Date(data.window_from).getTime();
  const to = Math.min(new Date(data.window_to).getTime(), Date.now());
  const unit = bucketUnit(to - from);
  const columns: Bucket[] = [];
  for (let t = startOf(from, unit, utc); t < to; t = next(t, unit, utc)) {
    columns.push({ start: t, counts: { SUCCESS: 0, DENIED: 0, FAILED: 0 }, total: 0 });
  }
  // Hours come sorted, so one pass walks the columns along with them.
  let i = 0;
  for (const h of data.hours) {
    const at = new Date(h.hour).getTime();
    while (i + 1 < columns.length && columns[i + 1]!.start <= at) i++;
    const column = columns[i];
    if (!column) break;
    addOutcomes(column, h.outcomes);
  }
  return { unit, columns };
}

function bucketLabel(start: number, unit: Unit, utc: boolean): string {
  const timeZone = utc ? 'UTC' : undefined;
  if (unit === 'hour') {
    return new Date(start).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone });
  }
  const day = new Date(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone });
  return unit === 'week' ? `Week of ${day}` : day;
}

function ActivityCard({ data, utc }: { data: Summary; utc: boolean }) {
  const { unit, columns } = buckets(data, utc);
  const peak = columns.reduce<Bucket | null>((best, c) => (c.total > (best?.total ?? 0) ? c : best), null);
  const max = peak?.total ?? 0;

  return (
    <div className="metric-tile usage-card">
      <div className="usage-card-head">
        <div className="usage-card-figure">
          <span className="metric-value">{formatInt(max)}</span>
          <span className="usage-card-of">peak per {unit}</span>
        </div>
        {peak && <span className="usage-card-of">{bucketLabel(peak.start, unit, utc)}</span>}
      </div>
      {max === 0 ? (
        <div className="sparkline sparkline-empty">Nothing recorded</div>
      ) : (
        <div className="sparkline" role="img" aria-label={`Entries per ${unit}, peak ${formatInt(max)}`}>
          {columns.map((c) => (
            <span
              key={c.start}
              className="spark-stack"
              style={{ height: `${(c.total / max) * 100}%` }}
              title={`${bucketLabel(c.start, unit, utc)}: ${OUTCOMES.map((o) => `${formatInt(c.counts[o.key])} ${o.label.toLowerCase()}`).join(', ')}`}
            >
              {OUTCOMES.map((o) =>
                c.counts[o.key] > 0 ? (
                  <span key={o.key} className={o.className} style={{ flexGrow: c.counts[o.key] }} />
                ) : null,
              )}
            </span>
          ))}
        </div>
      )}
      <ul className="usage-legend">
        {OUTCOMES.map((o) => (
          <li key={o.key}>
            <span className={`swatch ${o.className}`} />
            {o.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WhoCard({ data }: { data: Summary }) {
  const families = FAMILIES.map((f, i) => ({ ...f, index: i + 1, count: data.event_families[f.key] ?? 0 }));
  return (
    <div className="metric-tile usage-card">
      <div className="usage-card-head">
        <div className="usage-card-figure">
          <span className="metric-value">{formatInt(data.actors)}</span>
          <span className="usage-card-of">{data.actors === 1 ? 'actor' : 'actors'}</span>
        </div>
        <span className="usage-card-of">
          {formatInt(data.source_ips)} source {data.source_ips === 1 ? 'IP' : 'IPs'}
        </span>
      </div>
      <div
        className="stack-bar family-bar"
        role="img"
        aria-label={families.filter((f) => f.count > 0).map((f) => `${formatInt(f.count)} ${f.label}`).join(', ') || 'No entries'}
      >
        {families.map((f) => (
          <span key={f.key} className={`seg seg-family-${f.index}`} style={{ width: share(f.count, data.total) }} title={`${f.label}: ${formatInt(f.count)}`} />
        ))}
      </div>
      <ul className="usage-legend">
        {families
          .filter((f) => f.count > 0)
          .map((f) => (
            <li key={f.key}>
              <span className={`swatch seg-family-${f.index}`} />
              {f.label} <b>{formatInt(f.count)}</b>
            </li>
          ))}
      </ul>
      <p className="usage-card-note">
        <b className={data.failed_sign_ins > 0 ? 'usage-over' : undefined}>{formatInt(data.failed_sign_ins)}</b> failed
        sign-{data.failed_sign_ins === 1 ? 'in' : 'ins'}
      </p>
    </div>
  );
}
